import React, { useState, useEffect } from "react";
import sparklesSmallTokens from "@amzn/meridian-tokens/base/icon/sparkles-small";
import starFilledTokens from "@amzn/meridian-tokens/base/icon/star-filled";
import documentTokens from "@amzn/meridian-tokens/base/icon/document";
import clockTokens from "@amzn/meridian-tokens/base/icon/clock";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import { Button, Divider, Expander, Icon } from "@amzn/meridian";
import Loader from "@amzn/meridian/loader";
import {
  WasteProfilePathway,
  ClassifyWasteProfileResponse,
  SdsAttribute,
} from "src/types/classificationTypes";
import { classifyWasteProfile } from "src/services/classificationService";
import { useAppSelector } from "src/store/store";
import "./classification.scss";

function RemainingProfilesExpander({ remainingProfileIds }: { remainingProfileIds: string[] }) {
  const [expanded, setExpanded] = useState(false);
  if (remainingProfileIds.length === 0) return null;
  return (
    <>
      <Divider />
      <Expander title="View more profiles" type="inline" open={expanded} onChange={setExpanded}>
        <Column spacing="300" spacingInset="none">
          {remainingProfileIds.map((id, idx) => (
            <React.Fragment key={idx}>
              <Text type="h100">{id}</Text>
              {idx < remainingProfileIds.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </Column>
      </Expander>
    </>
  );
}

function ReasoningExpander({ reasoning }: { reasoning: string }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <Divider />
      <Expander title="Why these profiles?" type="inline" open={expanded} onChange={setExpanded}>
        <Text type="b200" color="secondary">
          {reasoning}
        </Text>
      </Expander>
    </>
  );
}

const SDS_LIST_COLLAPSE_THRESHOLD = 6;

function SdsAttributeValue({ value }: { value: string }) {
  const [expanded, setExpanded] = useState(false);
  const items = value.split(", ");
  if (items.length <= SDS_LIST_COLLAPSE_THRESHOLD) {
    return <Text type="b200">{value}</Text>;
  }
  const hiddenCount = items.length - SDS_LIST_COLLAPSE_THRESHOLD;
  const display = expanded
    ? value
    : `${items.slice(0, SDS_LIST_COLLAPSE_THRESHOLD).join(", ")}, +${hiddenCount} more`;
  return (
    <Column spacing="100" spacingInset="none" alignmentHorizontal="start">
      <Text type="b200">{display}</Text>
      <Button type="link" size="small" onClick={() => setExpanded((e) => !e)}>
        {expanded ? "Show less" : "Show all"}
      </Button>
    </Column>
  );
}

function SdsAttributesExpander({ sdsAttributes }: { sdsAttributes: SdsAttribute[] }) {
  const [expanded, setExpanded] = useState(false);
  if (sdsAttributes.length === 0) return null;
  return (
    <>
      <Divider />
      <Expander
        title="View SDS attributes"
        type="inline"
        spacingInset="none"
        open={expanded}
        onChange={setExpanded}
      >
        <Column width="100%" spacing="200" spacingInset="none">
          {sdsAttributes.map((attribute, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <Divider />}
              <Row width="100%" widths={["35%", "fill"]} alignmentVertical="top" spacing="300">
                <Text type="b200" color="secondary">
                  {attribute.name}
                </Text>
                <SdsAttributeValue value={attribute.value} />
              </Row>
            </React.Fragment>
          ))}
        </Column>
      </Expander>
    </>
  );
}

interface WasteProfileCardProps {
  asin: string;
  itemName?: string;
  onLoadingChange?: (value: boolean) => void;
  onPathwayChange?: (pathway: WasteProfilePathway) => void;
}

interface WasteProfileLayoutProps {
  headerClassName?: string;
  headerIcon?: object;
  headerLabel?: string;
  invertHeader?: boolean;
  recommendedWasteProfilesIds: string[];
  remainingProfiles: string[];
  reasoning?: string;
  sdsAttributes?: SdsAttribute[];
}

const WasteProfileLayout: React.FC<WasteProfileLayoutProps> = ({
  headerClassName = "",
  headerIcon,
  headerLabel,
  invertHeader = true,
  recommendedWasteProfilesIds,
  remainingProfiles,
  reasoning,
  sdsAttributes,
}) => (
  <Column className={`home-card ${headerClassName}`} spacingInset="400 none none">
    {headerIcon && headerLabel && (
      <Row alignmentVertical="center" spacing="300" spacingInset="none 500">
        {invertHeader ? (
          <Icon color="inverted" tokens={headerIcon} />
        ) : (
          <Icon tokens={headerIcon} />
        )}
        <Text type="h200" color={invertHeader ? "inverted" : "primary"}>
          {headerLabel}
        </Text>
      </Row>
    )}
    <Column className="home-card" spacingInset="none">
      <Column spacingInset="400 500" spacing="300">
        {recommendedWasteProfilesIds.map((profileId, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 && <Divider />}
            <Text type="h200">{profileId}</Text>
          </React.Fragment>
        ))}
        <RemainingProfilesExpander remainingProfileIds={remainingProfiles} />
        {sdsAttributes && <SdsAttributesExpander sdsAttributes={sdsAttributes} />}
        {reasoning && <ReasoningExpander reasoning={reasoning} />}
      </Column>
    </Column>
  </Column>
);

function WasteProfileCard({
  asin,
  itemName,
  onLoadingChange,
  onPathwayChange,
}: WasteProfileCardProps) {
  const { activeSiteCode } = useAppSelector((state) => state.user);
  const [wasteProfileData, setWasteProfileData] = useState<ClassifyWasteProfileResponse | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const fetchClassification = async () => {
      setIsLoading(true);
      setWasteProfileData(null);
      try {
        const res = await classifyWasteProfile({
          itemName: itemName ?? "",
          asin,
          siteName: activeSiteCode,
        });
        if (!cancelled) {
          setWasteProfileData(res);
          onPathwayChange?.(res.pathway);
        }
      } catch (error) {
        console.error("[WasteProfileCard] Failed to classify waste profile:", error);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          onLoadingChange?.(false);
        }
      }
    };

    onLoadingChange?.(true);
    fetchClassification();
    return () => {
      cancelled = true;
    };
  }, [asin, itemName, activeSiteCode, retryCount]);

  if (isLoading) {
    return (
      <Column className="home-card" spacingInset="400" spacing="300">
        <Row spacing="300" alignmentVertical="center">
          <Loader size="small" type="circular" />
          <Text type="b300" color="secondary">
            Loading waste profile...
          </Text>
        </Row>
      </Column>
    );
  }

  if (!wasteProfileData) {
    return (
      <Column className="home-card" spacingInset="400" spacing="300">
        <Text type="b300" color="error">
          Unable to load waste profile. Please try again.
        </Text>
        <Button onClick={() => setRetryCount((c) => c + 1)} size="small" type="secondary">
          Retry
        </Button>
      </Column>
    );
  }

  const { recommendedWasteProfilesIds, allWasteProfilesIds, pathway, reasoning, sdsAttributes } =
    wasteProfileData;

  const recommendedSet = new Set(recommendedWasteProfilesIds);
  const remainingProfiles = allWasteProfilesIds.filter((id) => !recommendedSet.has(id));

  if (pathway === WasteProfilePathway.GOLDEN_ASIN) {
    return (
      <WasteProfileLayout
        headerClassName="asin-waste-profile--golden"
        headerIcon={starFilledTokens}
        headerLabel="Golden ASIN Profile"
        invertHeader={false}
        recommendedWasteProfilesIds={allWasteProfilesIds}
        remainingProfiles={[]}
        reasoning={reasoning}
      />
    );
  }

  if (pathway === WasteProfilePathway.SDS_EXTRACTION) {
    return (
      <WasteProfileLayout
        headerClassName="asin-waste-profile--sds"
        headerIcon={documentTokens}
        headerLabel="SDS Attribute Based Recommendation"
        recommendedWasteProfilesIds={recommendedWasteProfilesIds}
        remainingProfiles={remainingProfiles}
        reasoning={reasoning}
        sdsAttributes={sdsAttributes}
      />
    );
  }

  if (pathway === WasteProfilePathway.HISTORICAL) {
    return (
      <WasteProfileLayout
        headerClassName="asin-waste-profile--historical"
        headerIcon={clockTokens}
        headerLabel="Historical Classification"
        recommendedWasteProfilesIds={recommendedWasteProfilesIds}
        remainingProfiles={remainingProfiles}
        reasoning={reasoning}
      />
    );
  }

  if (
    pathway === WasteProfilePathway.SIMILARITY ||
    pathway === WasteProfilePathway.AI_EXTERNAL_RESEARCH
  ) {
    return (
      <WasteProfileLayout
        headerClassName="asin-waste-profile"
        headerIcon={sparklesSmallTokens}
        headerLabel="Profile Recommended by AI"
        recommendedWasteProfilesIds={recommendedWasteProfilesIds}
        remainingProfiles={remainingProfiles}
        reasoning={reasoning}
      />
    );
  }

  // DEFAULT — no header
  return (
    <WasteProfileLayout
      recommendedWasteProfilesIds={recommendedWasteProfilesIds}
      remainingProfiles={remainingProfiles}
      reasoning={reasoning}
    />
  );
}

export default WasteProfileCard;
